import { TFunction } from 'i18next'
import jsPDF from 'jspdf'
import domtoimage from 'dom-to-image'
import { calculateAdjustedDimensions, calculateImageDimensions, calculateRatio, getPdfDimensions } from './dimensions'
import { cleanText } from 'src/Libs/utils'

const SMALL_PADDING = 12
const MEDIUM_PADDING = 24
const BIG_PADDING = 48
const PDF_MARGIN = 0.1
const MAX_ELEMENT_HEIGHT_PERCENTAGE = 0.5
const FULL_IMAGE_SIZE = 1
const HALF_IMAGE_SIZE = 0.5
const THIRD_IMAGE_SIZE = 0.35

/* add this class to every element you'd like to hide before printing PDF */
export const HIDE_ON_PRINT_CLASS = 'hide-on-print'

/**
 * Allows to print system chart data on a pdf. The pdf includes textual information about the installation as well as screenshots of graphs.
 *
 * @param rootElementId
 * @param chartName
 * @param installationId
 * @param storageId
 * @param lastDataFetchTimestamp
 * @param setDownloadLoading
 * @param t
 * @param hasPieCharts
 */
export const printChartData = (
  rootElementId: string,
  chartName: string | undefined,
  installationId: string | undefined,
  storageId: string | undefined,
  lastDataFetchTimestamp: string | undefined,
  setDownloadLoading: (value: boolean) => void,
  t: TFunction,
  hasPieCharts?: boolean
): void => {
  setDownloadLoading(true)
  const elementToPrint = document.getElementById(rootElementId)
  const elementsToHide = document.querySelectorAll<HTMLElement>(`.${HIDE_ON_PRINT_CLASS}`)
  if (elementsToHide) elementsToHide.forEach(s => (s.style.visibility = 'hidden'))

  if (elementToPrint) {
    // Clone the element to avoid modifications on the actual DOM and set a max width
    const clonedElement = elementToPrint.cloneNode(true) as HTMLElement
    document.body.appendChild(clonedElement)
    clonedElement.style.maxWidth = '1600px'

    domtoimage
      .toPng(clonedElement)
      .then((blob: string) => {
        fillPdfWithContent(clonedElement, blob, chartName, installationId, storageId, lastDataFetchTimestamp, t, hasPieCharts)
      })
      .catch(err => console.error(err))
      .finally(() => {
        document.body.removeChild(clonedElement)
        if (elementsToHide) elementsToHide.forEach(s => (s.style.visibility = 'visible'))
        setDownloadLoading(false)
      })
  } else {
    setDownloadLoading(false)
  }
}

/**
 * Fills a pdf with chart data content
 *
 * @param elementToPrint
 * @param blob
 * @param chartName
 * @param installationId
 * @param storageId
 * @param lastDataFetchTimestamp
 * @param t
 * @param hasPieCharts
 */
const fillPdfWithContent = (
  elementToPrint: HTMLElement,
  blob: string,
  chartName: string | undefined,
  installationId: string | undefined,
  storageId: string | undefined,
  lastDataFetchTimestamp: string | undefined,
  t: TFunction,
  hasPieCharts?: boolean
): void => {
  const image = new Image()
  image.src = blob
  const pdf = new jsPDF('l', 'px', 'a4')

  const title = chartName ?? ''
  const subTitle = `${t('systemDataHandler.chartHeader.header.text', { value1: installationId, value2: storageId })}`
  const footer = `${t('systemDataHandler.chartHeader.footer.export')}: ${lastDataFetchTimestamp}`
  const pdfName = buildPdfName(installationId, storageId, chartName)

  const { width: pdfWidth, height: pdfHeight, x: xPdfLeft, y: yPdfTop } = getPdfDimensions(pdf, PDF_MARGIN)

  const ySubTitle = yPdfTop + SMALL_PADDING

  const ratio = calculateRatio(pdfWidth, pdfHeight, elementToPrint)

  const {
    x: graphX,
    y: graphY,
    width: graphWidthOriginal,
    height: graphHeightOriginal,
  } = calculateImageDimensions(xPdfLeft, ySubTitle, SMALL_PADDING, ratio, elementToPrint, FULL_IMAGE_SIZE)

  const maxHeight = pdfHeight * MAX_ELEMENT_HEIGHT_PERCENTAGE
  const { height: graphHeight, width: graphWidth } = calculateAdjustedDimensions(graphHeightOriginal, graphWidthOriginal, maxHeight, pdfWidth)
  let pieHeight = 0

  if (hasPieCharts) {
    const { pieY: pieProductionY, pieHeight: pieProductionHeight } = addPieChartsToPdf(pdfWidth, pdfHeight, graphHeight, pdf, pdfName)
    pieHeight = pieProductionY + pieProductionHeight * 0.9
  }

  const xFooter = xPdfLeft
  const yFooter = yPdfTop + (hasPieCharts ? pieHeight + MEDIUM_PADDING : graphY + graphHeight)

  pdf.text(cleanText(title), xPdfLeft, yPdfTop)
  pdf.setFontSize(10)
  pdf.text(cleanText(subTitle), xPdfLeft, ySubTitle)
  pdf.addImage(blob, 'png', graphX, graphY, graphWidth, graphHeight)
  pdf.setFontSize(8)
  pdf.text(cleanText(footer), xFooter, yFooter)

  if (!hasPieCharts) pdf.save(pdfName)
}

/**
 * Adds the production and house consumption pie chart to the pdf.
 *
 * @param pdfWidth
 * @param pdfHeight
 * @param pieChartProduction
 * @param pieChartConsumptionHouse
 * @param graphHeight
 * @param pdf
 * @returns
 */
const addPieChartsToPdf = (
  pdfWidth: number,
  pdfHeight: number,
  graphHeight: number,
  pdf: jsPDF,
  pdfName: string
): { pieY: number; pieHeight: number } => {
  const pieChartSavings = document.getElementById('chart-pieSavings-1')
  const pieChartConsumptionHouse = document.getElementById('chart-pieConsumptionHouse-3')
  const pieChartProduction = document.getElementById('chart-pieProduction-2')
  const pieChartsArray: HTMLElement[] = [pieChartSavings, pieChartConsumptionHouse, pieChartProduction].filter(c => !!c)

  // Ensure that the pieCharts get their size reduced no matter the type of used pieChart
  const imageSize = pieChartsArray.length === 3 ? THIRD_IMAGE_SIZE : HALF_IMAGE_SIZE

  let pieY = 0
  let pieHeight = 0
  let currentX = 0

  const addAllCharts = pieChartsArray.map(pieChart => {
    if (pieChart === null) return Promise.resolve()

    // Clone the pie chart to avoid modifications on the actual DOM and set a max width
    const clonedElement = pieChart.cloneNode(true) as HTMLElement
    clonedElement.style.maxWidth = '490px'
    document.body.appendChild(clonedElement)

    const pieRatio = calculateRatio(pdfWidth, pdfHeight, clonedElement)
    const {
      x: pieChartX,
      y: pieChartY,
      width: pieChartWidth,
      height: pieChartHeight,
    } = calculateImageDimensions(currentX, graphHeight, BIG_PADDING, pieRatio, clonedElement, imageSize)

    // Update the total height and x & y position of the pie charts
    pieY = pieChartY
    pieHeight = pieChartHeight > pieHeight ? pieChartHeight : pieHeight
    currentX += pieChartWidth

    return domtoimage
      .toPng(clonedElement)
      .then((blob: string) => {
        const image = new Image()
        image.src = blob
        pdf.addImage(blob, 'png', pieChartX, pieChartY + MEDIUM_PADDING, pieChartWidth, pieChartHeight)
      })
      .catch(err => console.error(err))
      .finally(() => {
        document.body.removeChild(clonedElement)
      })
  })

  // Await all pie charts to be added to the pdf before saving it
  Promise.all(addAllCharts).then(() => {
    pdf.save(pdfName)
  })

  return { pieY, pieHeight }
}

/**
 * Create a pdf name without any white spaces
 * @param chartName
 * @returns
 */
export const buildPdfName = (installationId?: string, storageId?: string, chartName?: string): string => {
  const stringPartInBrackets = /\([^()]*\)/g
  const chartNameParts = chartName?.replace(stringPartInBrackets, '').split(' ').join('-')

  const parts = [installationId, storageId, chartNameParts]
    .filter(part => part && part.trim() !== '')
    .map(sanitizeString)
    .filter(Boolean)

  return `${parts.join('_')}.pdf`
}

export const sanitizeString = (str?: string): string => {
  if (!str) return ''
  return str
    .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u206F\u00AD\uFEFF]/g, '') // Remove invisible Unicode
    .replace(/[^\w\s-]/g, '') // Keep only word characters, spaces, and hyphens
    .trim()
}
